/**
 * Показ раздела «Финансы» без сервера: `VITE_MOCK_API=1`.
 *
 * Выдуманные цифры в форме ответов `finance-api` — ровно те поля, что
 * читают экраны дашборда. Чтобы смотреть экраны глазами, а не проверять
 * расчёт: считает сервер. Остальные адреса разбирает общий mock портала.
 */

import type { PortalRequest } from '../../api';

const FRESH = {
  headline: 'Данные на 16.09.2026, 08:50',
  details: ['Продажи: полные', 'Себестоимость: на 15.09'],
  stale: false,
  started: true,
};

const EXPLANATION = {
  formula: 'Выручка = Σ фискальных оплат закрытых чеков',
  text: 'Как посчитано\n  сумма фискальных оплат по закрытым чекам операционного дня',
  checks: 412,
  synced_at: '2026-09-16T05:50:00+00:00',
};

const iso = (d: Date) => d.toISOString().slice(0, 10);

function range(params: URLSearchParams): { from: string; to: string } {
  const to = params.get('to') ?? '2026-09-15';
  const from = params.get('from') ?? iso(new Date(new Date(to).getTime() - 6 * 86_400_000));
  return { from, to };
}

function metric(code: string, name: string, unit: string, value: string | null, extra: object = {}) {
  return {
    code,
    name,
    unit,
    value,
    reliable: true,
    period: { from: '2026-09-09', to: '2026-09-15' },
    comparisons: {},
    extras: {},
    rows: [],
    caveats: [],
    explanation: EXPLANATION,
    freshness: FRESH,
    ...extra,
  };
}

function days(from: string, to: string) {
  const out = [];
  for (let t = new Date(from).getTime(); t <= new Date(to).getTime(); t += 86_400_000) {
    const d = new Date(t);
    const weekend = d.getUTCDay() === 5 || d.getUTCDay() === 6;
    const revenue = (weekend ? 245_000 : 168_000) + (d.getUTCDate() % 5) * 6_300;
    out.push({ period_start: iso(d), revenue: `${revenue}.00`, ma7: null, checks: Math.round(revenue / 2150) });
  }
  return out;
}

const METRICS: Record<string, (p: URLSearchParams) => object> = {
  'revenue.by_category': () =>
    metric('revenue.by_category', 'Выручка по категориям', '₽', '1392400.00', {
      rows: [
        { category_code: '1001', name: 'Горячие блюда', amount: '412300.00' },
        { category_code: '1002', name: 'Паста и ризотто', amount: '286900.00' },
        { category_code: '1003', name: 'Пицца', amount: '231500.00' },
        { category_code: '1004', name: 'Вино', amount: '198700.00' },
        { category_code: '1005', name: 'Десерты', amount: '96400.00' },
        { category_code: '1006', name: 'Бар', amount: '166600.00' },
      ],
    }),
  'revenue.by_hour': () =>
    metric('revenue.by_hour', 'Выручка по часам', '₽', '1392400.00', {
      rows: [12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23].map((hour) => ({
        hour,
        revenue: `${[42, 88, 96, 61, 44, 70, 128, 186, 204, 171, 112, 58][hour - 12]}000.00`,
      })),
    }),
  'revenue.by_waiter': () =>
    metric('revenue.by_waiter', 'Выручка по официантам', '₽', '1392400.00', {
      rows: [
        { waiter_rk7_code: '11', name: 'Мария Лебедева', revenue: '318200.00' },
        { waiter_rk7_code: '12', name: 'Дмитрий Орлов', revenue: '296400.00' },
        { waiter_rk7_code: '14', name: 'Екатерина Павлова', revenue: '271900.00' },
        { waiter_rk7_code: '17', name: 'Артём Волков', revenue: '254300.00' },
        { waiter_rk7_code: '21', name: null, revenue: '251600.00' },
      ],
    }),
  'revenue.by_payment': () =>
    metric('revenue.by_payment', 'Выручка по способам оплаты', '₽', '1392400.00', {
      rows: [
        { name: 'Банковская карта', amount: '1104300.00' },
        { name: 'Наличные', amount: '176200.00' },
        { name: 'СБП', amount: '81900.00' },
        { name: 'ProBonus', amount: '30000.00' },
      ],
    }),
  'sales.top_items': () =>
    metric('sales.top_items', 'Блюда', '₽', null, {
      extras: { never_sold: 7 },
      rows: [
        { code: '2101', name: 'Паста Карбонара', amount: '98400.00', quantity: '123' },
        { code: '2204', name: 'Пицца Маргарита', amount: '76300.00', quantity: '109' },
        { code: '2310', name: 'Стейк Рибай', amount: '71800.00', quantity: '26' },
        { code: '2402', name: 'Тирамису', amount: '42100.00', quantity: '81' },
        { code: '2105', name: 'Ризотто с белыми грибами', amount: '39900.00', quantity: '48' },
      ],
    }),
  'check.average': () =>
    metric('check.average', 'Средний чек', '₽', '2148.00', { comparisons: { same_weekday: '4' } }),
  'check.per_guest': () => metric('check.per_guest', 'На гостя', '₽', '1236.00'),
  'check.depth': () => metric('check.depth', 'Позиций в чеке', 'шт', '4.3'),
  'check.structure': () => metric('check.structure', 'Доля напитков', '%', '27.4'),
  'check.distribution': () =>
    metric('check.distribution', 'Распределение чеков', 'шт', null, {
      rows: [
        { label: 'до 1 000 ₽', checks: 84 },
        { label: '1–2 тыс', checks: 231 },
        { label: '2–4 тыс', checks: 196 },
        { label: '4–8 тыс', checks: 97 },
        { label: 'от 8 тыс', checks: 40 },
      ],
    }),
  'cost.foodcost_pct': () =>
    metric('cost.foodcost_pct', 'Фудкост', '%', '20.2', {
      extras: {
        kitchen_pct: '22.8',
        bar_pct: '15.1',
        margin: '1111200',
        coverage_pct: '94.5',
        excluded: {
          zero_cost: {
            text: 'себестоимость ноль — пустая техкарта или нет запасов',
            dishes: 9,
            revenue: '11678',
            top: ['Тыквенный суп', 'Лимонад домашний'],
          },
        },
      },
    }),
  'cost.foodcost_by_category': () =>
    metric('cost.foodcost_by_category', 'Фудкост по категориям', '%', null, {
      rows: [
        { category_code: '1001', name: 'Горячие блюда', revenue: '412300', foodcost_pct: '26.4', margin: '303400' },
        { category_code: '1002', name: 'Паста и ризотто', revenue: '286900', foodcost_pct: '19.7', margin: '230400' },
        { category_code: '1004', name: 'Вино', revenue: '198700', foodcost_pct: '31.2', margin: '136700' },
      ],
    }),
  'margin.by_dish': () =>
    metric('margin.by_dish', 'Маржа по блюдам', '₽', null, {
      rows: [
        { code: '2310', name: 'Стейк Рибай', light: 'red', quantity: '26', revenue: '71800', avg_price: '2760', unit_cost: '1190', foodcost_pct: '43.1', contribution: '40860', flag: null },
        { code: '2105', name: 'Ризотто с белыми грибами', light: 'yellow', quantity: '48', revenue: '39900', avg_price: '831', unit_cost: '262', foodcost_pct: '31.5', contribution: '27300', flag: null },
        { code: '2101', name: 'Паста Карбонара', light: 'green', quantity: '123', revenue: '98400', avg_price: '800', unit_cost: '148', foodcost_pct: '18.5', contribution: '80200', flag: null },
        { code: '2502', name: 'Тыквенный суп', light: null, quantity: '19', revenue: '11210', flag: 'zero_cost', flag_text: 'пустая техкарта' },
      ],
    }),
  'menu.engineering': () =>
    metric('menu.engineering', 'Матрица меню', 'шт', '4', {
      extras: { star: 1, plowhorse: 1, puzzle: 1, dog: 1 },
      rows: [
        { code: '2101', name: 'Паста Карбонара', category: 'Паста', quadrant: 'star', quantity: '123', popularity_pct: '31.0', popularity_threshold_pct: '7.0', margin_pct: '81.5', margin_threshold_pct: '76.0', contribution: '80200' },
        { code: '2204', name: 'Пицца Маргарита', category: 'Пицца', quadrant: 'plowhorse', quantity: '109', popularity_pct: '28.0', popularity_threshold_pct: '7.0', margin_pct: '71.0', margin_threshold_pct: '76.0', contribution: '54100' },
        { code: '2310', name: 'Стейк Рибай', category: 'Горячее', quadrant: 'puzzle', quantity: '26', popularity_pct: '5.1', popularity_threshold_pct: '7.0', margin_pct: '56.9', margin_threshold_pct: '52.0', contribution: '40860' },
        { code: '2109', name: 'Паста с печенью', category: 'Паста', quadrant: 'dog', quantity: '9', popularity_pct: '2.3', popularity_threshold_pct: '7.0', margin_pct: '70.1', margin_threshold_pct: '76.0', contribution: '5100' },
      ],
    }),
  'menu.abc': () =>
    metric('menu.abc', 'ABC-анализ', 'шт', '4', {
      extras: { quantity_A: 2, quantity_B: 1, quantity_C: 1, revenue_A: 2, revenue_B: 1, revenue_C: 1, margin_A: 2, margin_B: 1, margin_C: 1 },
      rows: [
        { code: '2101', abc_quantity: 'A', abc_revenue: 'A', abc_margin: 'A' },
        { code: '2204', abc_quantity: 'A', abc_revenue: 'A', abc_margin: 'B' },
        { code: '2310', abc_quantity: 'B', abc_revenue: 'B', abc_margin: 'A' },
        { code: '2109', abc_quantity: 'C', abc_revenue: 'C', abc_margin: 'C' },
      ],
    }),
};

const CHECKS = Array.from({ length: 12 }, (_, i) => ({
  id: 9100 + i,
  uid: `A${9100 + i}`,
  business_date: '2026-09-15',
  closed_at: `2026-09-15T${String(9 + i).padStart(2, '0')}:1${i % 10}:00+00:00`,
  table: String(3 + (i % 9)),
  guests: 1 + (i % 4),
  waiter: ['Мария Лебедева', 'Дмитрий Орлов', 'Екатерина Павлова'][i % 3],
  waiter_code: String(11 + (i % 3)),
  amount_gross: `${1800 + i * 370}.00`,
  amount_discount: i % 5 === 0 ? '200.00' : '0.00',
  amount_net: `${1800 + i * 370 - (i % 5 === 0 ? 200 : 0)}.00`,
}));

const RULES = {
  rules: [
    { kind: 'operation.void_check', title: 'Аннулирование чека', type: 'C', threshold: '5000', threshold_pct: null, is_default: true, is_active: true, nl_source: '' },
    { kind: 'operation.discount', title: 'Крупная скидка', type: 'C', threshold: '3000', threshold_pct: null, is_default: false, is_active: true, nl_source: 'скидка дороже 3000' },
  ],
  targets: [
    { metric_code: 'check.average', title: 'Средний чек', unit: '₽', value: '2100', comparison: 'gte', description: 'не ниже 2 100 ₽', valid_from: '2026-09-10' },
  ],
  metrics: [
    { code: 'revenue.pulse', title: 'Пульс дня', unit: '₽' },
    { code: 'check.average', title: 'Средний чек', unit: '₽' },
  ],
};

export async function mock(path: string, options: PortalRequest): Promise<unknown | undefined> {
  const [pathname, search = ''] = path.split('?');
  if (!pathname.startsWith('/api/finance/')) return undefined;
  const params = new URLSearchParams(search);
  const method = options.method ?? 'GET';

  if (pathname === '/api/finance/overview') {
    const { from, to } = range(params);
    const rows = days(from, to);
    return {
      period: { from, to, days: rows.length, text: `${from} — ${to}` },
      grain: 'day',
      freshness: FRESH,
      started: true,
      tiles: [
        metric('revenue.pulse', 'Выручка за период', '₽', '1392400.00', { comparisons: { same_weekday: '6' } }),
        metric('check.average', 'Средний чек', '₽', '2148.00', { comparisons: { same_weekday: '-2' } }),
        metric('check.count', 'Чеков', 'шт', '648'),
        metric('cost.foodcost_pct', 'Фудкост', '%', '20.2'),
      ],
      series: metric('revenue.dynamics', 'Динамика выручки', '₽', '1392400.00', { extras: { grain: 'day' }, rows }),
    };
  }
  if (pathname === '/api/finance/metrics') {
    return {
      metrics: [
        { code: 'revenue.pulse', name: 'Пульс дня', unit: '₽', grain: 'day', category: 'revenue', dimension: '', formula: EXPLANATION.formula },
      ],
    };
  }
  const metricMatch = /^\/api\/finance\/metrics\/([^/]+)$/.exec(pathname);
  if (metricMatch) {
    const build = METRICS[decodeURIComponent(metricMatch[1])];
    return build ? build(params) : metric(metricMatch[1], metricMatch[1], '₽', null);
  }
  if (pathname === '/api/finance/checks') {
    const { from, to } = range(params);
    return {
      period: { from, to, text: `${from} — ${to}` },
      filters: { waiter: params.get('waiter'), category: params.get('category'), item: params.get('item') },
      filter_labels: {
        waiter: params.get('waiter') ? 'официант Мария Лебедева' : null,
        category: params.get('category') ? 'категория «Паста и ризотто»' : null,
        item: params.get('item') ? 'блюдо «Паста Карбонара»' : null,
      },
      total: CHECKS.length,
      offset: 0,
      page: 200,
      checks: CHECKS,
    };
  }
  const checkMatch = /^\/api\/finance\/checks\/(\d+)$/.exec(pathname);
  if (checkMatch) {
    const row = CHECKS.find((c) => c.id === Number(checkMatch[1])) ?? CHECKS[0];
    return {
      ...row,
      opened_at: row.closed_at,
      is_void: false,
      is_deleted: false,
      items: [
        { line_no: 1, name: 'Паста Карбонара', category: 'Паста', code: '2101', quantity: '1', price_unit: '800.00', amount_net: '800.00', is_modifier: false, is_deleted: false },
        { line_no: 2, name: 'Бокал кьянти', category: 'Вино', code: '4011', quantity: '2', price_unit: '650.00', amount_net: '1300.00', is_modifier: false, is_deleted: false },
      ],
      payments: [{ code: '1', name: 'Банковская карта', amount: row.amount_net }],
    };
  }
  if (pathname === '/api/finance/data') {
    return {
      freshness: FRESH,
      sources: [
        { scope: 'sales', started: true, at: '2026-09-16T05:50:00+00:00', synced_until: '2026-09-16T05:45:00+00:00', shift_closed: true, missing_documents: 0, completeness_pct: 100, issues: [] },
        { scope: 'inventory', started: true, at: '2026-09-16T01:10:00+00:00', synced_until: '2026-09-15T21:00:00+00:00', shift_closed: null, missing_documents: 0, completeness_pct: null, issues: ['Приходы в StoreHouse не заводились с 05.09 — себестоимость сентября занижена.'] },
      ],
    };
  }
  if (pathname === '/api/finance/alerts') {
    const { from, to } = range(params);
    return {
      period: { from, to },
      kinds: [
        { code: 'operation.void_check', title: 'Аннулирование чека', type: 'C' },
        { code: 'deviation.revenue', title: 'Выручка ниже нормы', type: 'B' },
      ],
      alerts: [
        { id: 1, kind: 'operation.void_check', title: 'Аннулирование чека', severity: 'high', status: 'sent', business_date: '2026-09-14', fired_at: '2026-09-14T19:34:00+00:00', text: '', headline: 'Аннулирование чека', body: 'Чек №1847 на 12 400 ₽\nОфициант: Дмитрий Орлов', mark: 'high', suppressed_reason: '' },
        { id: 2, kind: 'deviation.revenue', title: 'Выручка ниже нормы', severity: 'medium', status: 'suppressed', business_date: '2026-09-11', fired_at: '2026-09-12T06:00:00+00:00', text: '', headline: 'Выручка ниже нормы', body: 'Выручка 138 200 ₽ при норме 171 000 ₽', mark: 'medium', suppressed_reason: 'полнота данных 82 %' },
      ],
      suppressed: 1,
    };
  }
  if (pathname === '/api/finance/control/rules') {
    if (method === 'POST') return { kind: 'operation.void_check', threshold: '5000' };
    return RULES;
  }
  if (pathname === '/api/finance/control/targets') {
    return { metric_code: 'check.average', value: '2100', valid_from: '2026-09-17' };
  }
  return undefined;
}
